import React, { useCallback, useEffect, useState } from "react";
import { Patient } from "../../PatientCard";
import DocumentUploadModal from "./DocumentUploadModal";
import DocumentManager from "./DocumentManager";
import DocumentService, {
  PatientDocument,
} from "../../../../services/documentService";
import DocumentViewer from "./DocumentViewer";

interface PatientDocumentsTabProps {
  patient: Patient;
}

const PatientDocumentsTab: React.FC<PatientDocumentsTabProps> = ({
  patient,
}) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDocumentManager, setShowDocumentManager] = useState(false);
  const [documents, setDocuments] = useState<PatientDocument[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [statsLoading, setStatsLoading] = useState(false);
  const [selectedDocument, setSelectedDocument] =
    useState<PatientDocument | null>(null);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(
    window.innerWidth >= 768 && window.innerWidth < 1024
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDocumentUploaded = (documentId: number) => {
    // alert("Document(s) uploaded successfully!");
    setShowUploadModal(false);
    setRefreshTrigger((prev) => prev + 1);
    loadDocuments();
  };

  const loadDocuments = useCallback(async () => {
    if (!patient.id) return;

    setStatsLoading(true);
    try {
      const docs = await DocumentService.getPatientDocuments(patient.id);
      setDocuments(docs);
    } catch (error) {
      console.error("Failed to load documents for stats:", error);
    } finally {
      setStatsLoading(false);
    }
  }, [patient.id]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const calculateStats = () => {
    const totalDocuments = documents.length;
    const labResults = documents.filter(
      (doc) => doc.document_type === "lab_result"
    ).length;
    const imagingFiles = documents.filter(
      (doc) =>
        doc.document_type === "imaging" ||
        doc.document_type === "dicom" ||
        doc.document_type === "xray_image"
    ).length;
    const prescriptions = documents.filter(
      (doc) => doc.document_type === "prescription"
    ).length;
    const confidentialDocs = documents.filter(
      (doc) => doc.is_confidential
    ).length;
    const totalSizeBytes = documents.reduce(
      (sum, doc) => sum + doc.file_size,
      0
    );
    const totalSizeMB = Math.round((totalSizeBytes / (1024 * 1024)) * 10) / 10;

    return {
      totalDocuments,
      labResults,
      imagingFiles,
      prescriptions,
      confidentialDocs,
      totalSizeMB,
    };
  };

  const filterDocuments = () => {
    let filtered = documents;

    if (searchTerm) {
      filtered = filtered.filter(
        (doc) =>
          doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doc.file_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doc.tags?.some((tag) =>
            tag.toLowerCase().includes(searchTerm.toLowerCase())
          )
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(
        (doc) => doc.document_type === selectedCategory
      );
    }

    return filtered;
  };

  const handleViewDocument = (document: PatientDocument) => {
    setSelectedDocument(document);
    setShowDocumentViewer(true);
  };

  const handleDownloadDocument = async (patientDoc: PatientDocument) => {
    try {
      const { blob, filename } = await DocumentService.downloadDocument(
        patientDoc.id!
      );

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      let downloadFilename = filename;
      if (!downloadFilename.includes(".")) {
        const extension = patientDoc.file_name?.split(".").pop();
        if (extension) {
          downloadFilename += "." + extension;
        }
      }

      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(`Failed to download document: ${err.message}`);
    }
  };

  const handleDeleteDocument = async (document: PatientDocument) => {
    if (
      !window.confirm(`Are you sure you want to delete "${document.title}"?`)
    ) {
      return;
    }

    try {
      await DocumentService.deleteDocument(document.id!);
      setDocuments((prev) => prev.filter((doc) => doc.id !== document.id));
      // alert("Document deleted successfully");
      setRefreshTrigger((prev) => prev + 1);
    } catch (err: any) {
      alert(`Failed to delete document: ${err.message}`);
    }
  };

  const categories = [
    { value: "", label: "All Categories" },
    { value: "lab_result", label: "Lab Results" },
    { value: "imaging", label: "Imaging" },
    { value: "dicom", label: "DICOM/Medical Images" },
    { value: "prescription", label: "Prescriptions" },
    { value: "report", label: "Reports" },
    { value: "consent", label: "Consent Forms" },
    { value: "insurance", label: "Insurance" },
    { value: "referral", label: "Referrals" },
    { value: "other", label: "Other" },
  ];

  const containerStyle: React.CSSProperties = {
    padding: isMobile ? "12px" : isTablet ? "16px" : "24px",
    height: "100%",
    overflow: "auto",
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: isMobile ? "flex-start" : "center",
    marginBottom: isMobile ? "16px" : "24px",
    padding: isMobile ? "16px" : "20px",
    background: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "12px" : "0",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" = "primary"
  ): React.CSSProperties => ({
    padding: isMobile ? "10px 16px" : "12px 24px",
    borderRadius: "8px",
    border: "none",
    fontSize: isMobile ? "12px" : "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: variant === "primary" ? "#f59e0b" : "#e5e7eb",
    color: variant === "primary" ? "white" : "#374151",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flex: isMobile ? "1" : "0 0 auto",
  });

  return (
    <>
      <div style={containerStyle}>
        {/* Header with Action Buttons */}
        <div style={headerStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3
              style={{
                fontSize: isMobile ? "16px" : "20px",
                fontWeight: "600",
                margin: 0,
                marginBottom: "4px",
                color: "#1f2937",
              }}
            >
              Documents & Files
            </h3>
            <p
              style={{
                fontSize: isMobile ? "12px" : "14px",
                color: "#6b7280",
                margin: 0,
                wordBreak: "break-word",
              }}
            >
              Upload and manage documents for{" "}
              {patient.full_name ||
                `${patient.first_name} ${patient.last_name}`}
            </p>
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              width: isMobile ? "100%" : "auto",
            }}
          >
            <button
              style={buttonStyle("primary")}
              onClick={() => setShowUploadModal(true)}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.background = "#d97706";
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.background = "#f59e0b";
                }
              }}
            >
              Upload Document
            </button>
          </div>
        </div>

        {/* Document History */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "12px",
            padding: isMobile ? "16px" : "20px",
            marginBottom: isMobile ? "16px" : "24px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            border: "1px solid #e5e7eb",
            height: isMobile ? "calc(100vh - 300px)" : "600px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
              flexWrap: isMobile ? "wrap" : "nowrap",
              gap: isMobile ? "8px" : "0",
            }}
          >
            <h4
              style={{
                fontSize: isMobile ? "14px" : "16px",
                fontWeight: "600",
                margin: 0,
                color: "#1f2937",
              }}
            >
              Document History ({documents.length})
            </h4>
          </div>

          {/* Filters */}
          <div
            style={{
              display: "flex",
              gap: isMobile ? "8px" : "12px",
              marginBottom: "16px",
              flexShrink: 0,
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}
          >
            <input
              type="text"
              id="search_documents"
              name="search_documents"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                minWidth: isMobile ? "150px" : "200px",
                padding: isMobile ? "6px 10px" : "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: isMobile ? "12px" : "14px",
                outline: "none",
                transition: "border-color 0.2s ease",
              }}
            />

            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{
                padding: isMobile ? "6px 10px" : "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: isMobile ? "12px" : "14px",
                outline: "none",
                minWidth: isMobile ? "120px" : "180px",
              }}
            >
              {categories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          {/* Document List - Scrollable */}
          <div
            style={{
              flex: 1,
              overflow: "auto",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
            }}
          >
            {statsLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "30px",
                    height: "30px",
                    border: "3px solid #e2e8f0",
                    borderTop: "3px solid #f59e0b",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
                Loading documents...
              </div>
            ) : filterDocuments().length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  color: "#6b7280",
                  padding: isMobile ? "30px 20px" : "40px",
                  fontSize: isMobile ? "14px" : "16px",
                }}
              >
                {documents.length === 0 ? (
                  <>
                    No documents found for this patient
                    <br />
                    <p
                      style={{
                        fontSize: isMobile ? "12px" : "14px",
                        marginTop: "8px",
                      }}
                    >
                      Upload documents to get started
                    </p>
                  </>
                ) : (
                  <>
                    No documents match your search criteria
                    <br />
                    <p
                      style={{
                        fontSize: isMobile ? "12px" : "14px",
                        marginTop: "8px",
                      }}
                    >
                      Try adjusting your search or filter settings
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div>
                {!isMobile && (
                  /* List Header - Hide on mobile */
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isTablet
                        ? "auto 2fr 1fr auto"
                        : "auto 2fr 1fr 1fr 1fr auto",
                      gap: "16px",
                      padding: "12px 16px",
                      background: "#f8fafc",
                      borderBottom: "1px solid #e5e7eb",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#6b7280",
                      alignItems: "center",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <div>Type</div>
                    <div>Name</div>
                    <div>Category</div>
                    {!isTablet && <div>Size</div>}
                    {!isTablet && <div>Upload Date</div>}
                    <div>Actions</div>
                  </div>
                )}

                {/* Document Items */}
                {filterDocuments().map((document) => (
                  <div
                    key={document.id}
                    style={{
                      display: isMobile ? "block" : "grid",
                      gridTemplateColumns: isMobile
                        ? "none"
                        : isTablet
                        ? "auto 2fr 1fr auto"
                        : "auto 2fr 1fr 1fr 1fr auto",
                      gap: isMobile ? "8px" : "16px",
                      padding: isMobile ? "16px" : "12px 16px",
                      borderBottom: "1px solid #f3f4f6",
                      fontSize: isMobile ? "12px" : "14px",
                      alignItems: "center",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f8fafc")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    {isMobile ? (
                      // Mobile layout - stacked
                      <>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            marginBottom: "8px",
                          }}
                        >
                          <div style={{ fontSize: "20px", flexShrink: 0 }}>
                            {DocumentService.getFileTypeIcon(
                              document.mime_type
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: "500",
                                color: "#1f2937",
                                cursor: "pointer",
                                wordBreak: "break-word",
                                marginBottom: "2px",
                              }}
                              onClick={() => handleViewDocument(document)}
                            >
                              {document.title}
                              {document.is_confidential && (
                                <span
                                  style={{
                                    marginLeft: "6px",
                                    padding: "1px 4px",
                                    background: "#fee2e2",
                                    color: "#dc2626",
                                    borderRadius: "4px",
                                    fontSize: "9px",
                                    fontWeight: "500",
                                  }}
                                >
                                  Confidential
                                </span>
                              )}
                            </div>
                            {document.description && (
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#6b7280",
                                  lineHeight: "1.3",
                                }}
                              >
                                {document.description.length > 40
                                  ? `${document.description.substring(
                                      0,
                                      40
                                    )}...`
                                  : document.description}
                              </div>
                            )}
                          </div>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            fontSize: "11px",
                            color: "#6b7280",
                            marginBottom: "12px",
                            flexWrap: "wrap",
                            gap: "8px",
                          }}
                        >
                          <span
                            style={{
                              padding: "2px 6px",
                              borderRadius: "8px",
                              fontSize: "10px",
                              fontWeight: "500",
                              background:
                                DocumentService.getCategoryColor(
                                  document.document_type
                                ) + "20",
                              color: DocumentService.getCategoryColor(
                                document.document_type
                              ),
                            }}
                          >
                            {DocumentService.getCategoryIcon(
                              document.document_type
                            )}
                          </span>
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              fontSize: "10px",
                            }}
                          >
                            <span>
                              {DocumentService.formatFileSize(
                                document.file_size
                              )}
                            </span>
                            <span>
                              {DocumentService.formatDate(document.upload_date)}
                            </span>
                          </div>
                        </div>

                        <div style={{ display: "flex", gap: "6px" }}>
                          <button
                            onClick={() => handleViewDocument(document)}
                            style={{
                              ...buttonStyle("primary"),
                              flex: 1,
                              fontSize: "10px",
                              padding: "6px 8px",
                            }}
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleDownloadDocument(document)}
                            style={{
                              ...buttonStyle("secondary"),
                              flex: 1,
                              fontSize: "10px",
                              padding: "6px 8px",
                            }}
                          >
                            Download
                          </button>
                          <button
                            onClick={() => handleDeleteDocument(document)}
                            style={{
                              ...buttonStyle("secondary"),
                              fontSize: "10px",
                              padding: "6px",
                              background: "#ef4444",
                              color: "white",
                              flex: "0 0 auto",
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </>
                    ) : (
                      // Desktop/Tablet layout - grid
                      <>
                        <div style={{ fontSize: "20px" }}>
                          {DocumentService.getFileTypeIcon(document.mime_type)}
                        </div>

                        <div>
                          <div
                            style={{
                              fontWeight: "500",
                              color: "#1f2937",
                              marginBottom: "2px",
                              cursor: "pointer",
                            }}
                            onClick={() => handleViewDocument(document)}
                          >
                            {document.title}
                            {document.is_confidential && (
                              <span
                                style={{
                                  marginLeft: "6px",
                                  padding: "1px 4px",
                                  background: "#fee2e2",
                                  color: "#dc2626",
                                  borderRadius: "4px",
                                  fontSize: "10px",
                                  fontWeight: "500",
                                }}
                              >
                                Confidential
                              </span>
                            )}
                          </div>
                          {document.description && (
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#6b7280",
                                lineHeight: "1.3",
                              }}
                            >
                              {document.description.length > 50
                                ? `${document.description.substring(0, 50)}...`
                                : document.description}
                            </div>
                          )}
                        </div>

                        <div>
                          <span
                            style={{
                              padding: "2px 6px",
                              borderRadius: "8px",
                              fontSize: "11px",
                              fontWeight: "500",
                              background:
                                DocumentService.getCategoryColor(
                                  document.document_type
                                ) + "20",
                              color: DocumentService.getCategoryColor(
                                document.document_type
                              ),
                            }}
                          >
                            {DocumentService.getCategoryIcon(
                              document.document_type
                            )}{" "}
                            {DocumentService.getCategoryLabel(
                              document.document_type
                            )}
                          </span>
                        </div>

                        {!isTablet && (
                          <div style={{ fontSize: "12px", color: "#6b7280" }}>
                            {DocumentService.formatFileSize(document.file_size)}
                          </div>
                        )}

                        {!isTablet && (
                          <div style={{ fontSize: "12px", color: "#6b7280" }}>
                            {DocumentService.formatDate(document.upload_date)}
                          </div>
                        )}

                        <div style={{ display: "flex", gap: "4px" }}>
                          <button
                            onClick={() => handleViewDocument(document)}
                            style={{
                              ...buttonStyle("secondary"),
                              padding: "4px 8px",
                              fontSize: "11px",
                              background: "#f59e0b",
                              color: "white",
                            }}
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleDownloadDocument(document)}
                            style={{
                              ...buttonStyle("secondary"),
                              padding: "4px 8px",
                              fontSize: "11px",
                            }}
                          >
                            Download
                          </button>
                          <button
                            onClick={() => handleDeleteDocument(document)}
                            style={{
                              ...buttonStyle("secondary"),
                              padding: "4px 8px",
                              fontSize: "11px",
                              background: "#ef4444",
                              color: "white",
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Document Statistics */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "12px",
            padding: isMobile ? "16px" : "20px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            border: "1px solid #e5e7eb",
            marginBottom: isMobile ? "16px" : "24px",
          }}
        >
          <h4
            style={{
              fontSize: isMobile ? "14px" : "16px",
              fontWeight: "600",
              marginBottom: "16px",
              color: "#1f2937",
            }}
          >
            Document Overview
          </h4>

          {statsLoading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  border: "2px solid #e2e8f0",
                  borderTop: "2px solid #f59e0b",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto",
                }}
              />
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "repeat(2, 1fr)"
                  : isTablet
                  ? "repeat(3, 1fr)"
                  : "repeat(auto-fit, minmax(120px, 1fr))",
                gap: isMobile ? "12px" : "16px",
              }}
            >
              {(() => {
                const stats = calculateStats();
                return (
                  <>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: isMobile ? "20px" : "24px",
                          fontWeight: "700",
                          color: "#f59e0b",
                          marginBottom: "4px",
                        }}
                      >
                        {stats.totalDocuments}
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "11px" : "12px",
                          color: "#6b7280",
                        }}
                      >
                        Total Documents
                      </div>
                    </div>

                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: isMobile ? "20px" : "24px",
                          fontWeight: "700",
                          color: "#3b82f6",
                          marginBottom: "4px",
                        }}
                      >
                        {stats.labResults}
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "11px" : "12px",
                          color: "#6b7280",
                        }}
                      >
                        Lab Results
                      </div>
                    </div>

                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: isMobile ? "20px" : "24px",
                          fontWeight: "700",
                          color: "#8b5cf6",
                          marginBottom: "4px",
                        }}
                      >
                        {stats.imagingFiles}
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "11px" : "12px",
                          color: "#6b7280",
                        }}
                      >
                        Imaging Files
                      </div>
                    </div>

                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: isMobile ? "20px" : "24px",
                          fontWeight: "700",
                          color: "#10b981",
                          marginBottom: "4px",
                        }}
                      >
                        {stats.prescriptions}
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "11px" : "12px",
                          color: "#6b7280",
                        }}
                      >
                        Prescriptions
                      </div>
                    </div>

                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: isMobile ? "20px" : "24px",
                          fontWeight: "700",
                          color: "#ef4444",
                          marginBottom: "4px",
                        }}
                      >
                        {stats.confidentialDocs}
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "11px" : "12px",
                          color: "#6b7280",
                        }}
                      >
                        Confidential
                      </div>
                    </div>

                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: isMobile ? "20px" : "24px",
                          fontWeight: "700",
                          color: "#6b7280",
                          marginBottom: "4px",
                        }}
                      >
                        {stats.totalSizeMB} MB
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "11px" : "12px",
                          color: "#6b7280",
                        }}
                      >
                        Total Size
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Information Panel */}
        {/* <div
          style={{
            background: "#fffbeb",
            border: "1px solid #fde68a",
            borderRadius: "8px",
            padding: isMobile ? "12px" : "16px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: isMobile ? "16px" : "20px",
              marginBottom: "8px",
            }}
          >
            📁
          </div>
          <h4
            style={{
              fontSize: isMobile ? "14px" : "16px",
              fontWeight: "600",
              marginBottom: "8px",
              color: "#92400e",
            }}
          >
            Secure Document Management
          </h4>
          <p
            style={{
              fontSize: isMobile ? "12px" : "14px",
              color: "#92400e",
              marginBottom: "16px",
              lineHeight: "1.5",
            }}
          >
            Upload, organize, and securely manage all patient documents in one
            place. Support for multiple file types with advanced search and
            categorization features.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "8px",
              fontSize: isMobile ? "11px" : "12px",
              color: "#92400e",
            }}
          >
            <div>Drag & Drop Upload</div>
            <div>Multiple File Types</div>
            <div>Document Categorization</div>
            <div>Advanced Search</div>
            <div>Access Control</div>
            <div>Secure Storage</div>
          </div>
        </div> */}
      </div>

      {/* Document Upload Modal */}
      <DocumentUploadModal
        patient={patient}
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onDocumentUploaded={handleDocumentUploaded}
      />

      {/* Document Manager Modal */}
      <DocumentManager
        patient={patient}
        isOpen={showDocumentManager}
        onClose={() => setShowDocumentManager(false)}
        refreshTrigger={refreshTrigger}
      />

      {/* Document Viewer */}
      <DocumentViewer
        patientDocument={selectedDocument}
        isOpen={showDocumentViewer}
        onClose={() => {
          setShowDocumentViewer(false);
          setSelectedDocument(null);
        }}
      />

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          button:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }

          input:focus, select:focus {
            border-color: #f59e0b !important;
            box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1) !important;
          }

          @media (max-width: 768px) {
            button:hover:not(:disabled) {
              transform: none;
              box-shadow: none;
            }
          }
        `}
      </style>
    </>
  );
};

export default PatientDocumentsTab;
